/**
 * Teacher → Learning Materials  (Phase 3)
 *
 * Full LMS Material library with:
 *   • Chunked file upload (1 MB chunks → bypasses ingress 100 MB limits)
 *   • Version history + "Revert to v#"
 *   • 4-state visibility machine: draft / published / scheduled / hidden
 *   • Bulk publish / hide via multi-select toolbar
 *
 * Backend lives at /api/school/lms/materials/*  (routes/lms_materials.py)
 */
import React, { useMemo, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
  FileText, Upload, MoreVertical, Eye, EyeOff, Clock, Trash2, History,
  Download, Loader2, RotateCcw, Search, CheckCircle2, FileUp,
} from 'lucide-react';
import { portalApi } from './teacherApi';
import { getToken } from '@/lib/authToken';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const CHUNK_SIZE = 1024 * 1024; // 1 MB

const _token = () => {
  try {
    const pt = sessionStorage.getItem('school_preview_token');
    if (pt) return pt;
  } catch { /* noop */ }
  return getToken();
};

const STATUS_COLOR = {
  draft:     'bg-slate-100 text-slate-700 border-slate-200',
  published: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  scheduled: 'bg-amber-100  text-amber-800  border-amber-200',
  hidden:    'bg-rose-100   text-rose-700   border-rose-200',
};

const fmtSize = (b) => {
  if (!b) return '0 B';
  const u = ['B', 'KB', 'MB', 'GB']; let i = 0; let n = b;
  while (n >= 1024 && i < 3) { n /= 1024; i += 1; }
  return `${n.toFixed(1)} ${u[i]}`;
};

const fmtDateTime = (d) => d ? new Date(d).toLocaleString() : '';

/** Chunked uploader — calls onProgress(percent) during upload. */
const uploadFile = async ({ file, materialId, meta, onProgress }) => {
  const total_chunks = Math.max(1, Math.ceil(file.size / CHUNK_SIZE));
  const initBody = {
    filename: file.name,
    total_size: file.size,
    total_chunks,
    content_type: file.type || 'application/octet-stream',
    ...(materialId ? { material_id: materialId } : meta),
  };
  const initRes = await fetch(`${API_URL}/api/school/lms/materials/upload/init`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${_token()}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(initBody),
  });
  if (!initRes.ok) {
    const err = await initRes.json().catch(() => ({}));
    throw new Error(err.detail || 'Upload init failed');
  }
  const { upload_id, material_id: mid } = await initRes.json();
  for (let i = 0; i < total_chunks; i += 1) {
    const blob = file.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE);
    const fd = new FormData();
    fd.append('chunk', blob, `chunk-${i}`);
    const res = await fetch(`${API_URL}/api/school/lms/materials/upload/${upload_id}/chunk/${i}`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${_token()}` },
      body: fd,
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || `Chunk ${i} failed`);
    }
    onProgress?.(Math.round(((i + 1) / total_chunks) * 100));
  }
  const finRes = await fetch(`${API_URL}/api/school/lms/materials/upload/${upload_id}/complete`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${_token()}` },
  });
  if (!finRes.ok) {
    const err = await finRes.json().catch(() => ({}));
    throw new Error(err.detail || 'Finalisation failed');
  }
  return { ...(await finRes.json()), material_id: mid };
};

const downloadVersion = async (materialId, version, filename) => {
  const url = `${API_URL}/api/school/lms/materials/${materialId}/versions/${version}/download`;
  const res = await fetch(url, { headers: { Authorization: `Bearer ${_token()}` } });
  if (!res.ok) throw new Error('Download failed');
  const blob = await res.blob();
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = filename || `material-v${version}`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(a.href);
};

// ============================= Upload Dialog =============================
const UploadDialog = ({ open, onOpenChange, subjects, material, onDone }) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [subjectId, setSubjectId] = useState('');
  const [file, setFile] = useState(null);
  const [progress, setProgress] = useState(0);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  React.useEffect(() => {
    if (open) {
      setTitle(material?.title || '');
      setDescription(material?.description || '');
      setSubjectId(material?.subject_id || subjects[0]?.id || '');
      setFile(null); setProgress(0); setBusy(false);
    }
  }, [open, material, subjects]);

  const isNew = !material;
  const canSubmit = !!file && (isNew ? title.trim() && subjectId : true);

  const onSubmit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    try {
      const result = await uploadFile({
        file,
        materialId: material?.id,
        meta: isNew ? { title, description, subject_id: subjectId, class_ids: [], tags: [] } : null,
        onProgress: setProgress,
      });
      toast.success(isNew
        ? 'Material uploaded as draft. Hit "Publish" to make it visible.'
        : `Uploaded as version ${result.version}.`);
      onDone?.(result);
      onOpenChange(false);
    } catch (e) {
      toast.error(e.message || 'Upload failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="lms-upload-dialog">
        <DialogHeader>
          <DialogTitle>{isNew ? 'New material' : `Upload new version → ${material.title}`}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          {isNew && (
            <>
              <div>
                <Label className="text-xs">Title</Label>
                <Input value={title} onChange={(e) => setTitle(e.target.value)}
                       data-testid="lms-upload-title" placeholder="e.g. Algebra Worksheet 1" />
              </div>
              <div>
                <Label className="text-xs">Description (optional)</Label>
                <Input value={description} onChange={(e) => setDescription(e.target.value)}
                       data-testid="lms-upload-desc" />
              </div>
              <div>
                <Label className="text-xs">Subject</Label>
                <Select value={subjectId} onValueChange={setSubjectId}>
                  <SelectTrigger data-testid="lms-upload-subject">
                    <SelectValue placeholder="Pick a subject" />
                  </SelectTrigger>
                  <SelectContent>
                    {subjects.map(s => (
                      <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </>
          )}
          <div>
            <Label className="text-xs">File</Label>
            <div className="flex items-center gap-2">
              <input
                ref={fileRef} type="file" className="hidden"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                data-testid="lms-upload-file-input"
              />
              <Button type="button" size="sm" variant="outline"
                      onClick={() => fileRef.current?.click()}
                      data-testid="lms-upload-pick-file">
                <FileUp className="h-4 w-4 mr-1" /> Pick file
              </Button>
              <span className="text-xs text-slate-500 truncate">
                {file ? `${file.name} · ${fmtSize(file.size)}` : 'No file selected'}
              </span>
            </div>
          </div>
          {busy && (
            <div>
              <div className="text-xs mb-1 text-slate-600">Uploading… {progress}%</div>
              <div className="h-1.5 w-full bg-slate-100 rounded overflow-hidden">
                <div className="h-full bg-indigo-500 transition-all"
                     style={{ width: `${progress}%` }} />
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={onSubmit} disabled={!canSubmit || busy}
                  data-testid="lms-upload-submit">
            {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Upload className="h-4 w-4 mr-1" />}
            {isNew ? 'Upload draft' : 'Upload version'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ============================= Schedule Dialog =============================
const ScheduleDialog = ({ open, onOpenChange, material, onSubmit }) => {
  const [whenLocal, setWhenLocal] = useState('');
  React.useEffect(() => {
    if (open) {
      const d = new Date(Date.now() + 60 * 60 * 1000);
      const pad = (n) => String(n).padStart(2, '0');
      setWhenLocal(`${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`);
    }
  }, [open]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="lms-schedule-dialog">
        <DialogHeader>
          <DialogTitle>Schedule publish — {material?.title}</DialogTitle>
        </DialogHeader>
        <div>
          <Label className="text-xs">Publish at (your local time)</Label>
          <Input type="datetime-local" value={whenLocal}
                 onChange={(e) => setWhenLocal(e.target.value)}
                 data-testid="lms-schedule-when" />
          <p className="text-[11px] text-slate-500 mt-1">
            The material will auto-flip to <b>published</b> at that moment (server checks every minute).
          </p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={() => onSubmit(new Date(whenLocal).toISOString())}
                  data-testid="lms-schedule-submit">
            <Clock className="h-4 w-4 mr-1" /> Schedule
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ============================= Versions Dialog =============================
const VersionsDialog = ({ open, onOpenChange, material, onRevert }) => {
  if (!material) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="lms-versions-dialog">
        <DialogHeader>
          <DialogTitle>Version history — {material.title}</DialogTitle>
        </DialogHeader>
        <div className="space-y-2 max-h-80 overflow-auto">
          {(material.versions || []).slice().reverse().map(v => {
            const isCurrent = v.version === material.current_version;
            return (
              <div key={v.version}
                   className="flex items-center justify-between border rounded px-3 py-2"
                   data-testid={`lms-version-row-${v.version}`}>
                <div className="text-sm">
                  <div className="font-medium flex items-center gap-2">
                    v{v.version}
                    {isCurrent && (
                      <Badge className="bg-indigo-100 text-indigo-700 border-indigo-200">
                        current
                      </Badge>
                    )}
                  </div>
                  <div className="text-xs text-slate-500">
                    {v.filename} · {fmtSize(v.size_bytes)} · {fmtDateTime(v.uploaded_at)}
                  </div>
                </div>
                <div className="flex gap-1">
                  <Button size="sm" variant="outline"
                          onClick={() => downloadVersion(material.id, v.version, v.filename)
                            .catch(e => toast.error(e.message))}
                          data-testid={`lms-version-download-${v.version}`}>
                    <Download className="h-3.5 w-3.5" />
                  </Button>
                  {!isCurrent && (
                    <Button size="sm" variant="outline"
                            onClick={() => onRevert(v.version)}
                            data-testid={`lms-version-revert-${v.version}`}>
                      <RotateCcw className="h-3.5 w-3.5 mr-1" />Revert
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ============================= Material card =============================
const MaterialCard = ({ m, subjectName, selected, onToggle, actions }) => (
  <Card className="hover:shadow-md transition-shadow" data-testid={`lms-material-card-${m.id}`}>
    <CardContent className="pt-4 space-y-2">
      <div className="flex items-start gap-2">
        <Checkbox checked={selected} onCheckedChange={onToggle}
                  data-testid={`lms-material-select-${m.id}`}
                  className="mt-1" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <FileText className="h-4 w-4 text-indigo-500 shrink-0" />
            <h3 className="font-medium text-sm truncate">{m.title}</h3>
          </div>
          {m.description && (
            <p className="text-xs text-slate-500 line-clamp-2 mt-0.5">{m.description}</p>
          )}
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button size="icon" variant="ghost" className="h-7 w-7"
                    data-testid={`lms-material-menu-${m.id}`}>
              <MoreVertical className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-44">
            <DropdownMenuItem onClick={actions.upload}
                              data-testid={`lms-action-upload-${m.id}`}>
              <Upload className="h-4 w-4 mr-2" /> Upload new version
            </DropdownMenuItem>
            <DropdownMenuItem onClick={actions.versions} disabled={!(m.version_count > 0)}
                              data-testid={`lms-action-versions-${m.id}`}>
              <History className="h-4 w-4 mr-2" /> Version history
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={actions.publish}
                              disabled={m.status === 'published' || !(m.version_count > 0)}
                              data-testid={`lms-action-publish-${m.id}`}>
              <Eye className="h-4 w-4 mr-2" /> Publish
            </DropdownMenuItem>
            <DropdownMenuItem onClick={actions.schedule}
                              disabled={!(m.version_count > 0)}
                              data-testid={`lms-action-schedule-${m.id}`}>
              <Clock className="h-4 w-4 mr-2" /> Schedule…
            </DropdownMenuItem>
            <DropdownMenuItem onClick={actions.hide}
                              disabled={m.status === 'hidden'}
                              data-testid={`lms-action-hide-${m.id}`}>
              <EyeOff className="h-4 w-4 mr-2" /> Hide
            </DropdownMenuItem>
            <DropdownMenuItem onClick={actions.draft}
                              disabled={m.status === 'draft'}
                              data-testid={`lms-action-draft-${m.id}`}>
              <FileText className="h-4 w-4 mr-2" /> Move to draft
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={actions.del} className="text-red-600"
                              data-testid={`lms-action-delete-${m.id}`}>
              <Trash2 className="h-4 w-4 mr-2" /> Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <div className="flex items-center gap-2 flex-wrap text-xs">
        <Badge variant="outline" className={STATUS_COLOR[m.status] || STATUS_COLOR.draft}>
          {m.status}
        </Badge>
        {m.status === 'scheduled' && m.publish_at && (
          <span className="text-amber-700">→ {fmtDateTime(m.publish_at)}</span>
        )}
        <span className="text-slate-500">{subjectName}</span>
        <span className="text-slate-400">·</span>
        <span className="text-slate-500">{m.version_count} version{m.version_count !== 1 ? 's' : ''}</span>
      </div>
    </CardContent>
  </Card>
);

// ============================= Main page =============================
export const TeacherMaterials = () => {
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [filterSubject, setFilterSubject] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selected, setSelected] = useState(new Set());
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadTarget, setUploadTarget] = useState(null);  // null = new material
  const [scheduleTarget, setScheduleTarget] = useState(null);
  const [versionsTarget, setVersionsTarget] = useState(null);

  const { data: subjects = [] } = useQuery({
    queryKey: ['teacher-subjects-for-materials'],
    queryFn: () => portalApi('/teacher/subjects'),
  });
  const subjectName = useMemo(() => {
    const m = {}; subjects.forEach(s => { m[s.id] = s.name; }); return m;
  }, [subjects]);

  const { data: materials = [], isLoading, refetch } = useQuery({
    queryKey: ['lms-materials', filterSubject, filterStatus, search],
    queryFn: () => {
      const qs = new URLSearchParams();
      if (filterSubject !== 'all') qs.set('subject_id', filterSubject);
      if (filterStatus !== 'all') qs.set('status', filterStatus);
      if (search.trim()) qs.set('q', search.trim());
      qs.set('include_hidden', 'true');
      return fetch(`${API_URL}/api/school/lms/materials?${qs.toString()}`, {
        headers: { Authorization: `Bearer ${_token()}` },
      }).then(r => r.json());
    },
  });

  const callApi = async (path, opts = {}) => {
    const res = await fetch(`${API_URL}/api/school/lms${path}`, {
      ...opts,
      headers: {
        Authorization: `Bearer ${_token()}`,
        'Content-Type': 'application/json',
        ...(opts.headers || {}),
      },
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || `Request failed (${res.status})`);
    }
    return res.json();
  };

  const onPublish = useMutation({
    mutationFn: (id) => callApi(`/materials/${id}/publish`, { method: 'POST' }),
    onSuccess: () => { toast.success('Published'); refetch(); },
    onError: (e) => toast.error(e.message),
  });
  const onHide = useMutation({
    mutationFn: (id) => callApi(`/materials/${id}/hide`, { method: 'POST' }),
    onSuccess: () => { toast.success('Hidden'); refetch(); },
    onError: (e) => toast.error(e.message),
  });
  const onDraft = useMutation({
    mutationFn: (id) => callApi(`/materials/${id}/draft`, { method: 'POST' }),
    onSuccess: () => { toast.success('Moved to draft'); refetch(); },
    onError: (e) => toast.error(e.message),
  });
  const onDelete = useMutation({
    mutationFn: (id) => callApi(`/materials/${id}`, { method: 'DELETE' }),
    onSuccess: () => { toast.success('Deleted'); refetch(); },
    onError: (e) => toast.error(e.message),
  });
  const onSchedule = useMutation({
    mutationFn: ({ id, publish_at }) =>
      callApi(`/materials/${id}/schedule`, {
        method: 'POST', body: JSON.stringify({ publish_at }),
      }),
    onSuccess: () => { toast.success('Scheduled'); refetch(); setScheduleTarget(null); },
    onError: (e) => toast.error(e.message),
  });
  const onRevert = useMutation({
    mutationFn: ({ id, version }) =>
      callApi(`/materials/${id}/revert/${version}`, { method: 'POST' }),
    onSuccess: (data) => {
      toast.success(`Reverted to v${data.current_version}`);
      refetch();
      setVersionsTarget(prev => prev ? { ...prev, ...data } : null);
    },
    onError: (e) => toast.error(e.message),
  });
  const onBulk = useMutation({
    mutationFn: (action) =>
      callApi('/materials/bulk/visibility', {
        method: 'POST',
        body: JSON.stringify({ material_ids: Array.from(selected), action }),
      }),
    onSuccess: (r) => {
      toast.success(`${r.affected} updated · ${r.skipped} skipped`);
      setSelected(new Set());
      refetch();
    },
    onError: (e) => toast.error(e.message),
  });

  const toggleSelect = (id) => setSelected(s => {
    const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n;
  });

  return (
    <div className="space-y-4" data-testid="teacher-materials-page">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <FileText className="h-5 w-5 text-indigo-500" /> Learning Materials
          </h2>
          <p className="text-xs text-slate-500">
            Upload, version and publish learning files to your classes.
          </p>
        </div>
        <Button onClick={() => { setUploadTarget(null); setUploadOpen(true); }}
                data-testid="lms-new-material-btn">
          <Upload className="h-4 w-4 mr-1" /> New material
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-0 sm:min-w-[180px]">
          <Search className="h-3.5 w-3.5 absolute left-2 top-2.5 text-slate-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)}
                 placeholder="Search title, description, tags…"
                 className="pl-7 h-8" data-testid="lms-search" />
        </div>
        <Select value={filterSubject} onValueChange={setFilterSubject}>
          <SelectTrigger className="h-8 w-48" data-testid="lms-filter-subject">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All subjects</SelectItem>
            {subjects.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="h-8 w-40" data-testid="lms-filter-status">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="published">Published</SelectItem>
            <SelectItem value="scheduled">Scheduled</SelectItem>
            <SelectItem value="hidden">Hidden</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Bulk toolbar */}
      {selected.size > 0 && (
        <div className="flex items-center gap-2 bg-indigo-50 border border-indigo-200 rounded px-3 py-2"
             data-testid="lms-bulk-toolbar">
          <CheckCircle2 className="h-4 w-4 text-indigo-600" />
          <span className="text-sm font-medium">{selected.size} selected</span>
          <div className="ml-auto flex gap-2">
            <Button size="sm" variant="outline" onClick={() => onBulk.mutate('publish')}
                    disabled={onBulk.isPending}
                    data-testid="lms-bulk-publish">
              <Eye className="h-3.5 w-3.5 mr-1" /> Publish
            </Button>
            <Button size="sm" variant="outline" onClick={() => onBulk.mutate('hide')}
                    disabled={onBulk.isPending}
                    data-testid="lms-bulk-hide">
              <EyeOff className="h-3.5 w-3.5 mr-1" /> Hide
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())}>
              Clear
            </Button>
          </div>
        </div>
      )}

      {/* Grid */}
      {isLoading ? (
        <div className="text-center text-slate-500 py-10">
          <Loader2 className="h-6 w-6 animate-spin inline-block" />
        </div>
      ) : materials.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-sm text-slate-500">
          No materials yet — click <b>New material</b> to upload your first file.
        </CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {materials.map(m => (
            <MaterialCard
              key={m.id} m={m}
              subjectName={subjectName[m.subject_id] || ''}
              selected={selected.has(m.id)}
              onToggle={() => toggleSelect(m.id)}
              actions={{
                upload: () => { setUploadTarget(m); setUploadOpen(true); },
                versions: () => setVersionsTarget(m),
                publish: () => onPublish.mutate(m.id),
                hide: () => onHide.mutate(m.id),
                draft: () => onDraft.mutate(m.id),
                schedule: () => setScheduleTarget(m),
                del: () => { if (window.confirm(`Delete "${m.title}"?`)) onDelete.mutate(m.id); },
              }}
            />
          ))}
        </div>
      )}

      <UploadDialog
        open={uploadOpen} onOpenChange={setUploadOpen}
        subjects={subjects} material={uploadTarget}
        onDone={() => { qc.invalidateQueries({ queryKey: ['lms-materials'] }); }}
      />
      <ScheduleDialog
        open={!!scheduleTarget}
        onOpenChange={(v) => !v && setScheduleTarget(null)}
        material={scheduleTarget}
        onSubmit={(iso) => onSchedule.mutate({ id: scheduleTarget.id, publish_at: iso })}
      />
      <VersionsDialog
        open={!!versionsTarget}
        onOpenChange={(v) => !v && setVersionsTarget(null)}
        material={versionsTarget}
        onRevert={(v) => onRevert.mutate({ id: versionsTarget.id, version: v })}
      />
    </div>
  );
};

export default TeacherMaterials;
